import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Edit, FileText, FolderOpen, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  PLANTILLA_CATEGORIAS, PlantillaCategoria, PlantillaDocumento, usePlantillasDocumentos,
} from "@/hooks/usePlantillasDocumentos";

interface Props {
  /** When true, hides admin actions and only allows downloading templates. */
  readOnly?: boolean;
  /** Optional filter shown above the list. */
  showFilter?: boolean;
}

export function PlantillasDocumentosManager({ readOnly = false, showFilter = true }: Props) {
  const { perfil } = useAuth();
  const isAdmin = perfil?.rol === "admin";
  const canManage = !readOnly && isAdmin;
  const { plantillas, loading, upload, update, remove, download } = usePlantillasDocumentos();
  const { toast } = useToast();

  const [filterCat, setFilterCat] = useState<string>("all");
  const [filterTag, setFilterTag] = useState<string>("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PlantillaDocumento | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlantillaDocumento | null>(null);

  const filtered = useMemo(() => {
    return plantillas.filter((p) => {
      if (filterCat !== "all" && p.categoria !== filterCat) return false;
      if (filterTag && !(p.etiqueta ?? "").toLowerCase().includes(filterTag.toLowerCase())) return false;
      return true;
    });
  }, [plantillas, filterCat, filterTag]);

  const grouped = useMemo(() => {
    const map = new Map<PlantillaCategoria, PlantillaDocumento[]>();
    for (const p of filtered) {
      const arr = map.get(p.categoria) ?? [];
      arr.push(p);
      map.set(p.categoria, arr);
    }
    return map;
  }, [filtered]);

  const labelOf = (c: PlantillaCategoria) => PLANTILLA_CATEGORIAS.find((x) => x.value === c)?.label ?? c;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-primary" />
              Plantillas de entrega
            </CardTitle>
            <CardDescription>
              Repositorio de plantillas (certificados, checklists, boletines y manuales) disponibles para descarga al finalizar las obras.
            </CardDescription>
          </div>
          {canManage && (
            <Button onClick={() => { setEditing(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-1" />
              Subir plantilla
            </Button>
          )}
        </CardHeader>
        {showFilter && (
          <CardContent className="pt-0 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Categoría</Label>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {PLANTILLA_CATEGORIAS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Tipo de instalación / etiqueta</Label>
              <Input
                placeholder="Ej: intrusión, CCTV, incendio..."
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
              />
            </div>
          </CardContent>
        )}
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando plantillas...</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No hay plantillas {filterCat !== "all" || filterTag ? "que coincidan con el filtro" : "todavía"}.
            {canManage && " Pulsa 'Subir plantilla' para añadir la primera."}
          </CardContent>
        </Card>
      ) : (
        PLANTILLA_CATEGORIAS.map((cat) => {
          const items = grouped.get(cat.value) ?? [];
          if (items.length === 0) return null;
          return (
            <Card key={cat.value}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{cat.label}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {items.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 p-3 rounded-md border bg-muted/30">
                    <div className="flex items-start gap-3 min-w-0">
                      <FileText className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.nombre}</p>
                        {p.descripcion && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{p.descripcion}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {p.etiqueta && <Badge variant="secondary" className="text-[10px]">{p.etiqueta}</Badge>}
                          <span className="text-[10px] text-muted-foreground truncate">{p.nombre_archivo}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => download(p)} title="Descargar">
                        <Download className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setShowForm(true); }} title="Editar">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(p)} title="Eliminar">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}

      {canManage && (
        <PlantillaForm
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          editing={editing}
          onSubmitNew={async (v) => {
            try {
              await upload(v);
              toast({ title: "Plantilla subida" });
              setShowForm(false);
            } catch (e: any) {
              toast({ title: "Error", description: e.message, variant: "destructive" });
            }
          }}
          onSubmitEdit={async (id, patch) => {
            try {
              await update(id, patch);
              toast({ title: "Plantilla actualizada" });
              setShowForm(false);
              setEditing(null);
            } catch (e: any) {
              toast({ title: "Error", description: e.message, variant: "destructive" });
            }
          }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{deleteTarget?.nombre}" del repositorio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              if (!deleteTarget) return;
              try {
                await remove(deleteTarget);
                toast({ title: "Plantilla eliminada" });
              } catch (e: any) {
                toast({ title: "Error", description: e.message, variant: "destructive" });
              } finally {
                setDeleteTarget(null);
              }
            }}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PlantillaForm({
  open, onClose, editing, onSubmitNew, onSubmitEdit,
}: {
  open: boolean;
  onClose: () => void;
  editing: PlantillaDocumento | null;
  onSubmitNew: (v: { nombre: string; descripcion?: string; categoria: PlantillaCategoria; etiqueta?: string; file: File }) => Promise<void>;
  onSubmitEdit: (id: string, patch: Partial<Pick<PlantillaDocumento, "nombre" | "descripcion" | "categoria" | "etiqueta">>) => Promise<void>;
}) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [categoria, setCategoria] = useState<PlantillaCategoria>("certificado");
  const [etiqueta, setEtiqueta] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setNombre(editing.nombre);
        setDescripcion(editing.descripcion ?? "");
        setCategoria(editing.categoria);
        setEtiqueta(editing.etiqueta ?? "");
        setFile(null);
      } else {
        setNombre(""); setDescripcion(""); setCategoria("certificado"); setEtiqueta(""); setFile(null);
      }
    }
  }, [open, editing]);

  const reset = () => {
    setNombre(""); setDescripcion(""); setCategoria("certificado"); setEtiqueta(""); setFile(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar plantilla" : "Subir plantilla"}</DialogTitle>
          <DialogDescription>
            {editing ? "Actualiza los datos de la plantilla." : "Añade una nueva plantilla al repositorio de la empresa."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre *</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div>
            <Label>Categoría *</Label>
            <Select value={categoria} onValueChange={(v) => setCategoria(v as PlantillaCategoria)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLANTILLA_CATEGORIAS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo de instalación / etiqueta</Label>
            <Input
              placeholder="Ej: intrusión, CCTV, incendio, control accesos..."
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
            />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} />
          </div>
          {!editing && (
            <div>
              <Label>Archivo *</Label>
              <Input
                type="file"
                accept="application/pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); reset(); }}>Cancelar</Button>
          <Button
            disabled={submitting || !nombre || (!editing && !file)}
            onClick={async () => {
              setSubmitting(true);
              try {
                if (editing) {
                  await onSubmitEdit(editing.id, { nombre, descripcion, categoria, etiqueta });
                } else if (file) {
                  await onSubmitNew({ nombre, descripcion, categoria, etiqueta, file });
                }
                reset();
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? "Guardando..." : editing ? "Guardar" : "Subir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
